import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { resolveBusinessErrorMessage } from '../errors/business-error-messages';
import { SendApi } from '../apis/send-api';
import type {
  SendAuthTxnResult,
  SendGozemResponse,
  SendPspResponse,
  InitTransferParams,
  InitPspTransferParams,
  RecipientsResponse,
} from '../models/send.model';
import type { TopupMethod, TopupToken } from '../models/topup.model';

/**
 * SendService
 *
 * Business-logic layer between SendStore and SendApi.
 *
 * Responsibilities:
 *  - Unwrap API envelopes (`{ success, methods }` → `methods[]`)
 *  - Return raw API data; S3 URL resolution is done reactively in the UI layer
 *  - Keep SendStore free of direct API or environment dependencies
 *
 * Architecture: Component → Store → Service → Api → Backend
 */
@Injectable({
  providedIn: 'root',
})
export class SendService {
  private readonly api = inject(SendApi);

  // ── 1. Methods ─────────────────────────────────────────────────────────────

  /**
   * Fetch transfer methods for the given amount. Icons are returned as raw
   * relative paths; the S3 base URL is applied reactively in the UI layer so
   * icons resolve once runtime config loads.
   */
  getMethods(amount: number): Observable<TopupMethod[]> {
    return this.api.getMethods(amount).pipe(map((res) => res.methods));
  }

  // ── 2. Tokens ──────────────────────────────────────────────────────────────

  getTokens(processorId: string): Observable<TopupToken[]> {
    return this.api.getTokens(processorId).pipe(
      map((res) => {
        if (!res.success) {
          // HTTP 200 with `success: false` — the code is the only reliable signal.
          // Goes through the registry rather than an inline `=== '451'` so every
          // mapped business code gets its proper message here too.
          throw new Error(
            resolveBusinessErrorMessage(
              res.error_code,
              'Impossible de charger vos comptes enregistrés. Vérifiez votre connexion et réessayez.',
            ),
          );
        }
        return res.tokens ?? [];
      }),
    );
  }

  getRecipients(): Observable<TopupToken[]> {
    return this.api.getRecipients().pipe(
      map((res: RecipientsResponse) =>
        res.recent_beneficiaries.map(
          (r) =>
            ({
              _id: r.account_number,
              token_name: r.full_name,
              display_key: r.phone_number,
              logo_url: r.profile_picture,
              token_properties: { account_number: r.account_number },
              processor_id: '',
              token_service: 2,
              token_type: 0,
              status: true,
            }) satisfies TopupToken,
        ),
      ),
    );
  }

  // ── 3. Auth transaction ────────────────────────────────────────────────────

  /**
   * Authorize the transfer: compute fees, return categories and PIN flag.
   *
   * @param beneficiaryPhoneNumber  PSP path — phone extracted from the selected token.
   */
  authTxn(
    amount: number,
    processorId: string,
    methodId: string,
    beneficiaryPhoneNumber?: string,
    accountNumber?: string,
  ): Observable<SendAuthTxnResult> {
    return this.api
      .authTxn(amount, processorId, methodId, beneficiaryPhoneNumber, accountNumber)
      .pipe(
        map((res) => ({
          ...res.authorize_trxn,
          withdrawal_id: res.withdrawals._id,
          payee_gm_account_number: res.payee_gm_account_details?.account_number,
          // The API returns the beneficiary name as `customer_name`; `name` is a
          // resilience fallback for older / other-processor responses (GPGT-40760 follow-up).
          beneficiary_full_name:
            res.payee_gm_account_details?.customer_name ?? res.payee_gm_account_details?.name,
          beneficiary_phone_number: res.payee_gm_account_details?.mobile
            ? `+${res.payee_gm_account_details.mobile}`
            : undefined,
        })),
      );
  }

  // ── 3a. Init Transfer (Gozem Money) ────────────────────────────────────────

  initTransfer(params: InitTransferParams): Observable<SendGozemResponse> {
    return this.api.initTransfer(params);
  }

  // ── 3b. Init PSP Transfer ──────────────────────────────────────────────────

  initPspTransfer(params: InitPspTransferParams): Observable<SendPspResponse> {
    return this.api.initPspTransfer(params);
  }
}
